import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  Box,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Collapse,
  IconButton,
  TableContainer,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableLoadingWrapper from "components/templates/TableLoadingWrapper";
import AvailabilityService from "services/availability.service";
import DateService from "utils/date.util";
import Button from "components/atoms/Button";
import { availabilityActions } from "redux/slices/availability";
import SubscriptionService from "services/subscription.service";
import { MODAL, modalActions } from "redux/slices/modal";
import BlockComponent from "components/organisms/BlockComponent/BlockComponent";

const BRAND = "#520A76";
const LINE = "#f1ecf5";
const MUTED = "rgba(0, 0, 0, 0.53)";

const periodLabel = (count: number) =>
  `${count} busy period${count > 1 ? "s" : ""}`;

// "Riyadh , Ksa" -> "RK", "Jeddah" -> "J"
const initials = (name: string) =>
  name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const cardStyles = {
  mb: "12px",
  overflow: "hidden",
  borderRadius: "14px",
  backgroundColor: "#ffffff",
  border: `1px solid ${LINE}`,
};

const headerStyles = {
  gap: 1.5,
  px: 2.5,
  py: 2,
  display: "flex",
  cursor: "pointer",
  alignItems: "center",
  transition: "background-color 150ms ease-out",
  "&:hover": { backgroundColor: "#fbf9fc" },
};

const avatarStyles = {
  width: 38,
  height: 38,
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 700,
  color: "#ffffff",
  display: "flex",
  borderRadius: "10px",
  alignItems: "center",
  letterSpacing: "0.02em",
  justifyContent: "center",
  backgroundColor: BRAND,
};

const tableStyles = {
  "& th, & td": {
    px: 2.5,
    py: 1.75,
    fontSize: 14,
    borderBottom: `1px solid ${LINE}`,
  },
  "& thead th": {
    py: 1.25,
    fontSize: 11,
    fontWeight: 700,
    color: "#8d8595",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    backgroundColor: "#fcfbfd",
    borderTop: `1px solid ${LINE}`,
  },
  "& tbody tr": { transition: "background-color 150ms ease-out" },
  "& tbody tr:hover": { backgroundColor: "#fbf9fc" },
  "& tbody tr:last-of-type td": { borderBottom: 0 },
};

const pillStyles = {
  px: 1.25,
  py: 0.5,
  fontSize: 12,
  fontWeight: 600,
  color: "#A03F52",
  borderRadius: "999px",
  display: "inline-block",
  backgroundColor: "#FDF2F4",
  border: "1px solid #F7DFE4",
};

const iconButtonStyles = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  color: "rgba(0, 0, 0, 0.45)",
};

export default function AvailabilityList() {
  const dispatch = useAppDispatch();
  const availabilities = useAppSelector(
    (state) => state.availability.availabilities
  );
  const loading = useAppSelector((state) => state.availability.loading);
  // Absent means expanded — nothing is hidden until the user collapses it.
  const [collapsed, setCollapsed] = useState<{ [zoneId: string]: boolean }>({});

  useEffect(() => {
    AvailabilityService.getAllAvailabilities();
  }, []);

  // One group per zone, so a zone's classes are read together instead of
  // interleaved with every other zone's.
  const zoneGroups = useMemo(() => {
    const groups = new Map<string, { key: string; name: string; items: any[] }>();

    availabilities.forEach((availability: any) => {
      const zone = availability.zone_id || availability.city_id;
      const key = zone?._id || "unassigned";
      if (!groups.has(key))
        groups.set(key, { key, name: zone?.name || "Unassigned", items: [] });
      groups.get(key)?.items.push(availability);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        // Newest period first, matching the order a freshly added one lands in.
        items: [...group.items].sort(
          (a, b) =>
            new Date(b.holiday_interval?.start_time).getTime() -
            new Date(a.holiday_interval?.start_time).getTime()
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availabilities]);

  const allCollapsed =
    zoneGroups.length > 0 && zoneGroups.every((group) => collapsed[group.key]);

  const toggleAll = () => {
    if (allCollapsed) return setCollapsed({});
    setCollapsed(
      zoneGroups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {})
    );
  };

  const toggleZone = (key: string, isExpanded: boolean) =>
    setCollapsed((prev) => ({ ...prev, [key]: isExpanded }));

  const confirmDelete = (id: string) => {
    SubscriptionService.subscribe(() => {
      AvailabilityService.deleteAvailability(id);
    });
    dispatch(
      modalActions.openModal({
        width: "500px",
        type: MODAL.CONFIRMATION_FORM,
        data: {
          heading: "Delete holiday",
          message: "Do you really want to delete this?",
        },
      })
    );
  };

  return (
    <>
      {availabilities.length > 0 && (
        <div className="pagination-list">
          <p>
            {periodLabel(availabilities.length)} in {zoneGroups.length} zone
            {zoneGroups.length > 1 ? "s" : ""}
          </p>
          <Button size="small" variant="text" onClick={toggleAll}>
            {allCollapsed ? "Expand all" : "Collapse all"}
          </Button>
        </div>
      )}

      <TableLoadingWrapper
        container
        coloumns={4}
        loading={loading}
        length={availabilities.length}
        message="There are nothing to show now"
      >
        {zoneGroups.map((group) => {
          const isExpanded = !collapsed[group.key];
          return (
            <Box key={group.key} sx={cardStyles}>
              <Box
                sx={headerStyles}
                onClick={() => toggleZone(group.key, isExpanded)}
              >
                <Box sx={avatarStyles} aria-hidden="true">
                  {initials(group.name)}
                </Box>
                <Box>
                  <Box
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#1e1e1e",
                      textTransform: "capitalize",
                    }}
                  >
                    {group.name}
                  </Box>
                  <Box sx={{ fontSize: 12.5, color: MUTED }}>
                    {periodLabel(group.items.length)}
                  </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                    group.name
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleZone(group.key, isExpanded);
                  }}
                  sx={{ color: BRAND }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <TableContainer>
                  <Table
                    size="small"
                    aria-label={`${group.name} busy periods`}
                    sx={tableStyles}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell width="22%">Class</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell width="12%">Status</TableCell>
                        <TableCell width="14%" align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.items.map((availability: any) => (
                        <TableRow key={availability._id}>
                          <TableCell
                            sx={{ fontWeight: 600, color: "#1e1e1e" }}
                          >
                            {availability.name}
                          </TableCell>
                          <TableCell sx={{ color: "#1e1e1e" }}>
                            <Box
                              sx={{
                                gap: 0.75,
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                {DateService.getShortDateString(
                                  availability.holiday_interval.start_time
                                )}
                              </span>
                              <span style={{ color: MUTED }}>
                                {DateService.getTimeString(
                                  availability.holiday_interval.start_time
                                )}
                              </span>
                              <span style={{ color: "#c9c2ce" }}>&#8594;</span>
                              <span>
                                {DateService.getShortDateString(
                                  availability.holiday_interval.end_time
                                )}
                              </span>
                              <span style={{ color: MUTED }}>
                                {DateService.getTimeString(
                                  availability.holiday_interval.end_time
                                )}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box component="span" sx={pillStyles}>
                              Busy
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                gap: 0.5,
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <BlockComponent permission={"availability:edit"}>
                                <IconButton
                                  size="small"
                                  title="Edit"
                                  aria-label={`Edit ${availability.name} busy period`}
                                  onClick={() =>
                                    dispatch(
                                      availabilityActions.setAvailability(
                                        availability
                                      )
                                    )
                                  }
                                  sx={{
                                    ...iconButtonStyles,
                                    "&:hover": {
                                      color: BRAND,
                                      backgroundColor: "rgba(82, 10, 118, 0.08)",
                                    },
                                  }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </BlockComponent>
                              <BlockComponent permission={"availability:delete"}>
                                <IconButton
                                  size="small"
                                  title="Delete"
                                  aria-label={`Delete ${availability.name} busy period`}
                                  onClick={() =>
                                    confirmDelete(availability?._id)
                                  }
                                  sx={{
                                    ...iconButtonStyles,
                                    "&:hover": {
                                      color: "error.main",
                                      backgroundColor: "rgba(183, 33, 54, 0.08)",
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </BlockComponent>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          );
        })}
      </TableLoadingWrapper>
    </>
  );
}
